import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiConnector } from '@frontend/util/api-connector';
import { ApiSettings, protobufApi } from '@frontend/util/api-connector-no-auth';
import { CentralApiService } from 'grpc-gateway-generated';
import { isNotNullOrUndefined } from 'job-status-api';
import { filter, map, shareReplay } from 'rxjs';

type AuthBrokerEnv = {
  AUTH_BROKER_URL: string;
};

type ApiRequest = {};

type ApiResponse = {
  token: string;
  organizationUuid: string;
  advertiserId: string;
};

@Injectable({ providedIn: 'root' })
export class AuthBrokerService extends ApiConnector<AuthBrokerEnv> {
  protected baseUrl = this.env.AUTH_BROKER_URL;
  protected defaultSettings = ApiSettings({
    authorizationBearerToken: this.authService.actualUserHubIdpToken$.pipe(
      filter(isNotNullOrUndefined),
    ),
  });
  protected centralServiceApi = protobufApi(CentralApiService);

  public getToken = this.endpoint<ApiRequest, ApiResponse>(
    this.centralServiceApi.ApiWithCognito,
  );

  private tokenCheck$ = this.getToken().response$.pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /**
   * Validates the advertiser ID - accepts both numeric IDs and UUIDs
   * @param id
   * @private
   */
  private isValidAdvertiserId(id: string | undefined): boolean {
    if (id === undefined || id === null || id === '') {
      return false;
    }

    // Accept numeric IDs (legacy advertiser IDs)
    if (!isNaN(Number(id)) && Number(id) > 0) {
      return true;
    }

    // Accept UUID format (organizationUuid fallback)
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
  }

  /**
   * Gets the advertiser ID synchronously, defaulting to null if invalid.
   * Returns as string to support both numeric IDs and UUID formats.
   * @param id
   * @private
   */
  private getAdvertiserIdSync(id: string | undefined): string | null {
    return this.isValidAdvertiserId(id) ? (id as string) : null;
  }

  /**
   * Observable of the token string
   */
  token$ = this.tokenCheck$.pipe(
    map((res) => res?.token || ''),
    filter(isNotNullOrUndefined),
  );
  /**
   * Observable of the organization UUID
   */
  orgUuid$ = this.tokenCheck$.pipe(
    map((res) => res?.organizationUuid || ''),
    filter(isNotNullOrUndefined),
  );
  /**
   * Observable of the advertiser ID (string to support both numeric and UUID formats)
   * Returns string | null - numeric IDs or organizationUuid fallback
   */
  advertiserId$ = this.tokenCheck$.pipe(
    map((res) => this.getAdvertiserIdSync(res?.advertiserId)),
  );
  /**
   * Observable of the complete request options for CX Air API calls
   */
  cxAirRequest$ = this.tokenCheck$.pipe(
    filter(isNotNullOrUndefined),
    map((res) => ({
      headers: new HttpHeaders({
        Authorization: `Bearer ${res?.token}`, // If you need to hardcode a token for testing, do it here
      }),
      advertiserId: this.getAdvertiserIdSync(res?.advertiserId),
      organizationUuid: res?.organizationUuid || '',
    })),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
}
